// Modifier Filtering Logic
// Filters modifiers based on CPT category, site of service, and laterality

import type {
  CptCategory,
  SiteOfService,
  Laterality,
  ModifierFilterResult,
  ModifierValidationResult,
  CptTagMapEntry,
} from './types';
import {
  IMAGING_MODIFIERS,
  EVAL_MGMT_MODIFIERS,
  UNKNOWN_MODIFIERS,
  SURGICAL_OFFICE_MODIFIERS,
  SURGICAL_FACILITY_MODIFIERS,
  LATERALITY_MODIFIERS,
} from './types';
import { getCptTag, isCptExplicitlyTagged } from './cptTagMap';

/**
 * Get filtered modifiers for a CPT code based on category, site of service, and laterality
 */
export function getFilteredModifiers(
  cptCode: string,
  siteOfService: SiteOfService | null,
  selectedLaterality: Laterality | null
): ModifierFilterResult {
  const cptTag = getCptTag(cptCode, siteOfService || undefined);
  const isUnknown = cptTag.category === 'UNKNOWN' || !isCptExplicitlyTagged(cptCode);
  
  // Get base modifiers for category + site
  let baseModifiers = getBaseModifiersForCategory(cptTag, siteOfService);
  
  // Apply laterality filtering
  const { lateralityModifiers, nonLateralityModifiers } = separateLateralityModifiers(
    baseModifiers,
    cptTag.laterality_required,
    selectedLaterality
  );
  
  // Combine allowed modifiers
  let allowedModifiers: string[];
  if (cptTag.laterality_required) {
    // Include laterality modifiers based on selection
    allowedModifiers = [...lateralityModifiers, ...nonLateralityModifiers];
  } else {
    // No laterality modifiers unless user opts in
    allowedModifiers = nonLateralityModifiers;
  }
  
  return {
    allowedModifiers,
    lateralityRequired: cptTag.laterality_required,
    lateralityModifiers,
    nonLateralityModifiers,
    showLateralityToggle: !cptTag.laterality_required,
    modifierReviewRequired: isUnknown,
    validationMessage: isUnknown 
      ? 'CPT not in verified list. LINK will review modifiers during submission.'
      : undefined,
  };
}

/**
 * Get base modifiers based on CPT category and site of service
 */
function getBaseModifiersForCategory(
  cptTag: CptTagMapEntry,
  siteOfService: SiteOfService | null
): string[] {
  switch (cptTag.category) {
    case 'IMAGING':
      // Imaging: professional/technical split plus specific distinct-service modifiers.
      const imagingMods = [...IMAGING_MODIFIERS];
      if (cptTag.laterality_required) {
        imagingMods.push('RT', 'LT', '50');
      }
      return imagingMods;
    
    case 'EVAL_MGMT':
      // E&M: postoperative/significant separate E&M plus specific distinct-service modifiers.
      return [...EVAL_MGMT_MODIFIERS];
    
    case 'SURGICAL':
      if (siteOfService === 'office') {
        // Office surgical: limited set
        const officeMods = [...SURGICAL_OFFICE_MODIFIERS];
        if (cptTag.laterality_required) {
          officeMods.push('RT', 'LT', '50');
        }
        return officeMods;
      } else {
        // Hospital/ASC: full surgical set (no 26/TC unless imaging)
        return [...SURGICAL_FACILITY_MODIFIERS];
      }
    
    case 'OFFICE_PROC':
      // Office procedures: similar to office surgical
      const officeProcMods = [...SURGICAL_OFFICE_MODIFIERS];
      if (cptTag.laterality_required) {
        officeProcMods.push('RT', 'LT', '50');
      }
      return officeProcMods;
    
    case 'UNKNOWN':
    default:
      // Unknown: conservative set + flag for review
      return [...UNKNOWN_MODIFIERS];
  }
}

/**
 * Separate laterality modifiers and apply laterality-based filtering
 */
function separateLateralityModifiers(
  modifiers: string[],
  lateralityRequired: boolean,
  selectedLaterality: Laterality | null
): { lateralityModifiers: string[]; nonLateralityModifiers: string[] } {
  const lateralitySet = new Set(LATERALITY_MODIFIERS);
  
  const nonLateralityModifiers = modifiers.filter(m => !lateralitySet.has(m as any));
  
  // Determine which laterality modifiers are allowed based on selection
  let lateralityModifiers: string[] = [];
  
  if (lateralityRequired && selectedLaterality) {
    switch (selectedLaterality) {
      case 'bilateral':
        // Only 50 is allowed when bilateral is selected
        lateralityModifiers = modifiers.includes('50') ? ['50'] : [];
        break;
      case 'left':
        // Only LT is allowed, 50 and RT are blocked
        lateralityModifiers = modifiers.includes('LT') ? ['LT'] : [];
        break;
      case 'right':
        // Only RT is allowed, 50 and LT are blocked
        lateralityModifiers = modifiers.includes('RT') ? ['RT'] : [];
        break;
    }
  }
  
  return { lateralityModifiers, nonLateralityModifiers };
}

/**
 * Validate selected modifiers for a procedure
 */
export function validateModifiers(
  cptCode: string,
  selectedModifiers: string[],
  siteOfService: SiteOfService | null,
  selectedLaterality: Laterality | null
): ModifierValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  const filterResult = getFilteredModifiers(cptCode, siteOfService, selectedLaterality);
  const cptTag = getCptTag(cptCode, siteOfService || undefined);
  
  // Check for invalid modifiers
  const invalidModifiers = selectedModifiers.filter(
    m => !filterResult.allowedModifiers.includes(m) && 
         !filterResult.lateralityModifiers.includes(m) &&
         !filterResult.nonLateralityModifiers.includes(m)
  );
  
  if (invalidModifiers.length > 0) {
    errors.push(`Invalid modifiers for this CPT/site: ${invalidModifiers.join(', ')}`);
  }
  
  // Laterality validation
  if (cptTag.laterality_required) {
    const hasRT = selectedModifiers.includes('RT');
    const hasLT = selectedModifiers.includes('LT');
    const has50 = selectedModifiers.includes('50');
    
    // Check for conflicting laterality modifiers
    if ((hasRT && hasLT) || (hasRT && has50) || (hasLT && has50)) {
      errors.push('Cannot combine RT, LT, and 50 modifiers. Choose one laterality.');
    }
    
    // Check laterality matches selection
    if (selectedLaterality === 'bilateral' && (hasRT || hasLT)) {
      errors.push('RT/LT modifiers cannot be used with bilateral selection.');
    }
    if (selectedLaterality === 'left' && (hasRT || has50)) {
      errors.push('RT/50 modifiers cannot be used with left laterality.');
    }
    if (selectedLaterality === 'right' && (hasLT || has50)) {
      errors.push('LT/50 modifiers cannot be used with right laterality.');
    }
  }
  
  // Imaging-specific: 26 and TC cannot be used together
  if (cptTag.category === 'IMAGING') {
    if (selectedModifiers.includes('26') && selectedModifiers.includes('TC')) {
      errors.push('Cannot use both 26 (Professional) and TC (Technical) modifiers together.');
    }
  }
  
  // Add warnings for unknown CPTs
  if (filterResult.modifierReviewRequired) {
    warnings.push('This CPT code requires LINK review. Modifiers will be verified before submission.');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    modifierReviewRequired: filterResult.modifierReviewRequired,
  };
}

/**
 * Get auto-applied laterality modifier based on procedure laterality selection
 */
export function getAutoLateralityModifier(laterality: Laterality): string | null {
  switch (laterality) {
    case 'left': return 'LT';
    case 'right': return 'RT';
    case 'bilateral': return '50';
    default: return null;
  }
}

/**
 * Check if a modifier is a laterality modifier
 */
export function isLateralityModifier(modifier: string): boolean {
  return LATERALITY_MODIFIERS.includes(modifier as any);
}
